import type { PopupArrowEdge, PopupArrowMode, PopupArrowPlacement } from '@/shared/types/popup';

const MARGIN = 14;

/** Separación mínima entre el trigger y la punta de la flecha (eje hacia el trigger). */
export const POPOVER_ARROW_MIN_GAP_TO_TRIGGER_PX = 6;

/**
 * Extensión de la flecha fuera del rectángulo del panel hacia el trigger (incluye laterales;
 * el acople visual en `left`/`right` se ajusta en `PopupArrow` desplazando el beak hacia el panel).
 */
export const POPUP_ARROW_OVERHANG_PX = 11;

/**
 * Se **resta** del inset lateral del beak en `PopupArrow` (bordes `left`/`right` del panel).
 * No va en `usePopup`: un extra en `left` a menudo lo anula el clamp al viewport y parece que
 * «no se mueve nada»; aquí el cambio es siempre visible (más pico hacia el trigger al subir el valor).
 */
export const POPOVER_ARROW_SIDE_GAP_EXTRA_PX = -1.5;

type PopupPosition =
  | 'top'
  | 'top-left'
  | 'top-right'
  | 'bottom'
  | 'left'
  | 'right'
  | 'bottom-right'
  | 'bottom-center'
  | 'bottom-left';

function edgeFromPosition(position: PopupPosition): PopupArrowEdge {
  switch (position) {
    case 'top':
    case 'top-left':
    case 'top-right':
      return 'bottom';
    case 'left':
      return 'right';
    case 'right':
      return 'left';
    case 'bottom':
    case 'bottom-center':
    case 'bottom-left':
    case 'bottom-right':
    default:
      return 'top';
  }
}

function clampOffset(offset: number, length: number): number {
  return Math.min(Math.max(offset, MARGIN), Math.max(MARGIN, length - MARGIN));
}

/**
 * Flecha en modo manual según alineación sobre el borde del panel.
 */
export function placementFromManualMode(
  mode: Exclude<PopupArrowMode, false | 'auto'>,
  panelWidth: number,
  panelHeight: number,
): PopupArrowPlacement {
  const { edge, align, offsetPx: extra = 0 } = mode;
  if (edge === 'top' || edge === 'bottom') {
    let offset = MARGIN + extra;
    if (align === 'center') {
      offset = panelWidth / 2 + extra;
    } else if (align === 'end') {
      offset = panelWidth - MARGIN + extra;
    }
    return { edge, offsetPx: clampOffset(offset, panelWidth) };
  }
  let offset = MARGIN + extra;
  if (align === 'center') {
    offset = panelHeight / 2 + extra;
  } else if (align === 'end') {
    offset = panelHeight - MARGIN + extra;
  }
  return { edge, offsetPx: clampOffset(offset, panelHeight) };
}

/**
 * Flecha automática hacia el centro del trigger según la posición del popup.
 */
export function placementFromAutoMode(
  position: PopupPosition,
  triggerRect: DOMRect,
  panelLeft: number,
  panelTop: number,
  panelWidth: number,
  panelHeight: number,
): PopupArrowPlacement {
  const edge = edgeFromPosition(position);
  const cx = triggerRect.left + triggerRect.width / 2;
  const cy = triggerRect.top + triggerRect.height / 2;

  if (edge === 'top' || edge === 'bottom') {
    const offsetPx = clampOffset(cx - panelLeft, panelWidth);
    return { edge, offsetPx };
  }
  const offsetPx = clampOffset(cy - panelTop, panelHeight);
  return { edge, offsetPx };
}

export function resolvePopupArrowPlacement(
  mode: PopupArrowMode,
  position: PopupPosition,
  triggerRect: DOMRect,
  panelLeft: number,
  panelTop: number,
  panelWidth: number,
  panelHeight: number,
  /** Con posicionamiento absoluto respecto al padre, `auto` no puede apuntar al trigger con precisión. */
  absolute: boolean,
): PopupArrowPlacement | null {
  if (!mode) {
    return null;
  }
  if (mode === 'auto') {
    if (absolute) {
      return null;
    }
    return placementFromAutoMode(
      position,
      triggerRect,
      panelLeft,
      panelTop,
      panelWidth,
      panelHeight,
    );
  }
  return placementFromManualMode(mode, panelWidth, panelHeight);
}
